import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import T from 'i18n-react';
import { useRouter } from 'next/router';

import { FiltersIcon } from '@motorway/mw-highway-code';
import { ButtonToggle } from '@motorway/mw-highway-code/pre-alpha';

import { isCompletedPage } from 'Components/Account/Purchases/Purchases.helpers';
import useAccountRouter from 'Components/Account/useAccountRouter';
import cypressIds from 'Components/cypress_ids.json';
import { triggerFiltersPopoverClickEvent } from 'Services/analytics/events';
import { Popover } from 'Storybook/core/Popover';
import Separator from 'Storybook/core/Separator/Separator';
import { Tooltip } from 'Storybook/core/Tooltip';
import { applyCypressData } from 'Utilities/helpers';

import DateRangeRadioGroup from './components/DateRangeRadioGroup/DateRangeRadioGroup';
import DeliveredToggle from './components/DeliveredToggle/DeliveredToggle';
import { checkFiltersAreSelected } from './FiltersPopover.helpers';
import texts from './FiltersPopover.texts';

import styles from './FiltersPopover.module.scss';

T.setTexts(texts);

export const FiltersPopover: FC = () => {
	const [open, setOpen] = useState(false);
	const isNextOpenPrevented = useRef(false);

	const {
		query: { ...filtersQuery },
	} = useRouter();

	const { slug } = useAccountRouter();

	const selectedFiltersCount = checkFiltersAreSelected(filtersQuery);

	useEffect(() => {
		if (open) {
			triggerFiltersPopoverClickEvent({ slug });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const tooltipContent = selectedFiltersCount
		? String(
				T.translate(texts.activeFiltersTooltipLabel, {
					activeFilters: selectedFiltersCount,
					pluralExtension: selectedFiltersCount > 1 ? 's' : '',
				}),
		  )
		: String(T.translate(texts.tooltipLabel));

	return (
		<Tooltip
			content={tooltipContent}
			onFocus={(event) => {
				if (isNextOpenPrevented.current) {
					isNextOpenPrevented.current = false;
					event.preventDefault();
				}
			}}
			open={open ? false : undefined}
		>
			<div className={styles.filtersPopoverWrap}>
				<Popover
					modal
					content={
						<div className={styles.popoverFiltersContent}>
							<DateRangeRadioGroup />
							{isCompletedPage(slug) && (
								<>
									<Separator height="tall" width="full" />
									<DeliveredToggle />
								</>
							)}
						</div>
					}
					contentAlign="center"
					contentClassName={styles.popoverContent}
					contentOnPointerDownOutside={() => {
						// If the popover has `modal` set to `true`, it will focus back
						// the trigger by default.
						isNextOpenPrevented.current = true;
					}}
					contentSide="bottom"
					contentSideOffset={12}
					onOpenChange={setOpen}
					open={open}
					trigger={
						<ButtonToggle
							{...applyCypressData(cypressIds.buttons.accountFiltersPopoverTrigger)}
							data-testid="filters-popover-button"
							icon={FiltersIcon}
							label={String(T.translate(texts.popoverTrigger))}
							onClick={() => {
								setOpen(true);
							}}
							selected={selectedFiltersCount > 0}
							showLabel={false}
							size="medium"
							type="button"
							variant="secondary"
						/>
					}
				/>
			</div>
		</Tooltip>
	);
};
